import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditorMarkdown from "../kit/EditorMarkdown";
import { useEvaluadores } from "../../queries/useEvaluadores";
import { useComision, useConfirmarComision, useEnviarEvaluacion, useEvaluacion } from "../../queries/useInstancias";
import { InstanciaPaso } from "../../types/instanciaPaso";
import { mensajeDeError } from "../../utils/errores";
import { formatFechaHora, tiempoRestante } from "../../utils/fecha";

/**
 * Coordinador: conforma o edita la comisión de un proceso.
 *
 * Ofrece la sugerencia (afinidad y carga) más quienes ya la integran, y deja agregar a
 * cualquier otro evaluador habilitado: la sugerencia trae justo los que hacen falta, y
 * editar una comisión es casi siempre cambiar a alguien por otro que no sale ahí.
 *
 * Sacar a quien ya evaluó no borra su evaluación —se guarda—, pero deja de contar para
 * el resultado. Se avisa antes de guardar.
 */
export function ConformarComisionDialog({ it, onClose }: { it: InstanciaPaso; onClose: () => void }) {
  const { data, isLoading } = useComision(it.id, true);
  const confirmar = useConfirmarComision();
  const [sel, setSel] = useState<number[]>([]);
  const [agregados, setAgregados] = useState<{ id: number; nombre: string; carga: number }[]>([]);
  const [q, setQ] = useState("");
  const { data: catalogo = [] } = useEvaluadores(q, { ano: it.ano, semestre: it.semestre });

  // Preselecciona los actuales, o los primeros `tamano` sugeridos si aún no hay comisión.
  useEffect(() => {
    if (!data) return;
    const ya = data.actuales.map((a) => a.id);
    setSel(ya.length ? ya : data.sugeridos.slice(0, data.tamano).map((s) => s.id));
  }, [data]);

  const candidatos: { id: number; nombre: string; carga?: number }[] = [];
  for (const c of [...(data?.actuales ?? []), ...(data?.sugeridos ?? []), ...agregados]) {
    if (!candidatos.some((x) => x.id === c.id)) candidatos.push(c);
  }
  const bloqueados = new Set([...(data?.excluidos ?? []), ...candidatos.map((c) => c.id)]);
  const opciones = catalogo.filter((e) => e.puede_comision && e.vigente && !bloqueados.has(e.id));
  // Quienes ya evaluaron y quedarían fuera: su evaluación se guarda pero no cuenta.
  const salenConEvaluacion = (data?.actuales ?? []).filter((a) => a.evaluo && !sel.includes(a.id));
  const editando = (data?.actuales.length ?? 0) > 0;

  const toggle = (id: number) => setSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editando ? "Editar comisión" : "Conformar comisión"} — {it.paso}
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>
          Se necesitan <strong>{data?.tamano ?? "…"}</strong> evaluadores. La sugerencia optimiza áreas y equilibra la carga.
        </DialogContentText>
        {confirmar.isError && <Alert severity="error" sx={{ mb: 2 }}>{mensajeDeError(confirmar.error, "No se pudo confirmar.")}</Alert>}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Stack>
            {candidatos.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No hay evaluadores sugeridos (revisa el tamaño de comisión del paso y el catálogo de evaluadores).
              </Typography>
            )}
            {candidatos.map((c) => (
              <FormControlLabel
                key={c.id}
                control={<Checkbox checked={sel.includes(c.id)} onChange={() => toggle(c.id)} />}
                label={
                  <Typography variant="body2">
                    {c.nombre}
                    {c.carga !== undefined && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        · carga {c.carga}
                      </Typography>
                    )}
                    {data?.actuales.some((a) => a.id === c.id && a.evaluo) && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        · ya evaluó
                      </Typography>
                    )}
                  </Typography>
                }
              />
            ))}
            <Autocomplete
              sx={{ mt: 1.5 }}
              options={opciones}
              getOptionLabel={(e) => `${e.nombre} (${e.carga})`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={null}
              onChange={(_, e) => {
                if (!e) return;
                setAgregados((prev) => [...prev, { id: e.id, nombre: e.nombre, carga: e.carga }]);
                setSel((prev) => [...prev, e.id]);
                setQ("");
              }}
              inputValue={q}
              onInputChange={(_, value, reason) => reason !== "reset" && setQ(value)}
              filterOptions={(x) => x}
              noOptionsText={q ? "Sin evaluadores disponibles" : "Escribe para buscar"}
              renderInput={(params) => <TextField {...params} size="small" label="Agregar otro evaluador" placeholder="Buscar…" />}
            />
            {salenConEvaluacion.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {salenConEvaluacion.map((a) => a.nombre).join(", ")} ya {salenConEvaluacion.length === 1 ? "evaluó" : "evaluaron"}. Si
                {salenConEvaluacion.length === 1 ? " sale" : " salen"} de la comisión, la evaluación se guarda pero deja de contar para el
                resultado.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirmar.isPending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={confirmar.isPending || sel.length === 0}
          onClick={() => confirmar.mutate({ id: it.id, ids: sel }, { onSuccess: onClose })}
        >
          {editando ? "Guardar comisión" : "Confirmar comisión"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Integrante de la comisión: registra su evaluación (resultado único + apoyo). */
export function EvaluarDialog({ it, onClose }: { it: InstanciaPaso; onClose: () => void }) {
  const { data, isLoading } = useEvaluacion(it.id, true);
  const enviar = useEnviarEvaluacion();
  const [respuestas, setRespuestas] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (data) setRespuestas(data.mi_evaluacion?.respuestas ?? {});
  }, [data]);

  const set = (id: number, valor: unknown) => setRespuestas((prev) => ({ ...prev, [id]: valor }));
  // La pregunta de resultado es la que calcula la nota del proceso: sin ella no se
  // puede publicar, así que tampoco se envía la evaluación sin responderla.
  const resultado = data?.pregunta_resultado ?? null;
  const sinResultado = resultado !== null && !respuestas[resultado];

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Evaluar — {it.paso}</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {it.fecha_entrega_correccion && (
              <Typography variant="body2" color="text.secondary">
                Plazo de corrección: <strong>{formatFechaHora(it.fecha_entrega_correccion)}</strong>
                {tiempoRestante(it.fecha_entrega_correccion) && ` (${tiempoRestante(it.fecha_entrega_correccion)})`}
              </Typography>
            )}
            {enviar.isError && <Alert severity="error">{mensajeDeError(enviar.error, "No se pudo enviar la evaluación.")}</Alert>}
            {data?.instrucciones && <Alert severity="info">{data.instrucciones}</Alert>}

            {/* Las preguntas SON la evaluación: una define el resultado del proceso y
                el resto es lo que el estudiante va a leer. */}
            {(data?.preguntas ?? []).map((p) => (
              <Box key={p.id}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {p.texto}
                  {p.id === resultado && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      · define el resultado
                    </Typography>
                  )}
                </Typography>
                {p.tipo === "escala" && (
                  <TextField
                    type="number"
                    size="small"
                    value={(respuestas[p.id] as number | string) ?? ""}
                    onChange={(e) => set(p.id, e.target.value)}
                    inputProps={{ min: 1, max: 7, step: 0.1 }}
                    sx={{ width: 140 }}
                  />
                )}
                {p.tipo === "opcion" && (
                  <TextField select size="small" value={(respuestas[p.id] as string) ?? ""} onChange={(e) => set(p.id, e.target.value)} sx={{ minWidth: 280 }}>
                    {p.opciones.map((o) => (
                      <MenuItem key={o.texto} value={o.texto}>
                        {o.texto}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {/* Grande y con formato: una corrección de informe son varios párrafos,
                    con listas de lo que hay que arreglar, y en seis líneas de texto
                    plano no se escribía cómoda. */}
                {p.tipo === "texto" && (
                  <EditorMarkdown
                    placeholder="Lo que escribas acá lo lee el estudiante."
                    value={(respuestas[p.id] as string) ?? ""}
                    onChange={(texto) => set(p.id, texto)}
                    disabled={enviar.isPending}
                  />
                )}
              </Box>
            ))}
            {(data?.preguntas ?? []).length === 0 && (
              <Alert severity="warning">Este paso todavía no tiene preguntas de evaluación; el coordinador las configura en Pasos.</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviar.isPending}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={enviar.isPending || sinResultado}
          onClick={() =>
            enviar.mutate({ id: it.id, respuestas }, { onSuccess: onClose })
          }
        >
          {enviar.isPending ? "Enviando…" : "Enviar evaluación"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
